/**
 * Hook that syncs Word native comments (#-tag shortcuts) to KobSign backend.
 *
 * WordApi 1.4+ is required for `body.getComments()`. If not available,
 * the hook does nothing (graceful degradation).
 *
 * Flow:
 * 1. Poll `body.getComments()` every 5 seconds
 * 2. Parse each comment for #-tag prefix
 * 3. Create KobSign backend comment via API
 * 4. Insert Content Control at the comment's anchor
 * 5. Delete the native Word comment (replaced by CC + KobSign comment)
 * 6. Track synced comment IDs to avoid duplicates
 */

import { useEffect, useRef, useCallback, useState } from 'react'
import { parseHashTag } from '../services/hashTagParser'
import { createComment } from '../services/commentsApi'
import { getVersionParagraphs, type ParagraphMapEntry } from '../services/negotiationsApi'
import { insertCommentControl } from './useCommentControls'
import { devLog } from '../services/devLog'

interface UseWordCommentSyncOptions {
  negotiationId: number
  currentVersion: number
  knownTags: string[]
  /** Party colors indexed by lowercase tag_name */
  tagColors: Record<string, string>
  /** Default color for unknown tags */
  defaultColor?: string
  /** Enable/disable the sync loop */
  enabled?: boolean
  /** Callback when a comment is synced */
  onCommentSynced?: () => void
}

interface WordCommentSyncState {
  isActive: boolean
  isSupported: boolean
  syncedCount: number
}

const POLL_INTERVAL_MS = 5000

/**
 * Check if WordApi 1.4 comment API is available.
 * Uses feature detection rather than version check.
 */
async function isCommentApiSupported(): Promise<boolean> {
  try {
    return await Word.run(async (context) => {
      // WordApi 1.4 added getComments on Body
      const body = context.document.body
      return typeof (body as any).getComments === 'function'
    })
  } catch {
    return false
  }
}

export function useWordCommentSync({
  negotiationId,
  currentVersion: _currentVersion,
  knownTags,
  tagColors,
  defaultColor = '#ADB5BD',
  enabled = true,
  onCommentSynced,
}: UseWordCommentSyncOptions): WordCommentSyncState {
  const [isSupported, setIsSupported] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [syncedCount, setSyncedCount] = useState(0)

  const syncedIdsRef = useRef(new Set<string>())
  const isSyncingRef = useRef(false)
  const onCommentSyncedRef = useRef(onCommentSynced)
  onCommentSyncedRef.current = onCommentSynced
  const paraMapRef = useRef<ParagraphMapEntry[]>([])
  const paraMapFetchedRef = useRef(false)

  // Check support on mount
  useEffect(() => {
    if (!enabled) return
    isCommentApiSupported().then((supported) => {
      setIsSupported(supported)
      if (!supported) {
        devLog.info('WordCommentSync', 'WordApi 1.4 comment API not available — sync disabled')
      }
    })
  }, [enabled])

  // Sync a single Word comment
  const syncComment = useCallback(
    async (context: Word.RequestContext, comment: any): Promise<boolean> => {
      try {
        comment.load('id,content,contentRange')
        await context.sync()

        const commentId = String(comment.id)
        if (syncedIdsRef.current.has(commentId)) return false

        const text = comment.content?.trim()
        if (!text) return false

        const parsed = parseHashTag(text, knownTags)
        if (!parsed) return false // Not a #-tag comment

        devLog.info('WordCommentSync', `Found #-tag comment: #${parsed.tag} "${parsed.body.substring(0, 30)}"`)

        // Get anchor text from the comment's content range
        let anchorText = ''
        let anchorParagraph: number | undefined
        try {
          const range = comment.contentRange
          range.load('text')

          const paragraphs = range.paragraphs
          paragraphs.load('items')
          await context.sync()

          anchorText = range.text?.trim() || ''

          // Estimate paragraph index
          if (paragraphs.items.length > 0) {
            const bodyParagraphs = context.document.body.paragraphs
            bodyParagraphs.load('items')
            const firstPara = paragraphs.items[0]
            firstPara.load('uniqueLocalId')
            await context.sync()

            for (const bp of bodyParagraphs.items) {
              bp.load('uniqueLocalId')
            }
            await context.sync()

            for (let i = 0; i < bodyParagraphs.items.length; i++) {
              if (bodyParagraphs.items[i].uniqueLocalId === firstPara.uniqueLocalId) {
                anchorParagraph = i
                break
              }
            }
          }
        } catch {
          // Anchor extraction may fail — continue without anchor
        }

        // Determine visibility and target_tag for backend
        const targetTag = parsed.visibility === 'SPECIFIC' ? parsed.tag : undefined

        // Look up para_id from paragraph map
        let paraId: string | undefined
        if (anchorParagraph !== undefined && paraMapRef.current.length > 0) {
          const entry = paraMapRef.current.find(p => p.index === anchorParagraph)
          paraId = entry?.para_id
        }

        // Create KobSign comment via backend
        const backendComment = await createComment(negotiationId, {
          content: parsed.body || text,
          visibility: parsed.visibility,
          target_tag: targetTag,
          anchor_text: anchorText,
          anchor_paragraph: anchorParagraph,
          para_id: paraId,
        })

        devLog.info('WordCommentSync', `Created backend comment id=${backendComment.id}, tag=${backendComment.word_cc_tag}`)

        // Insert Content Control at the comment's anchor
        if (backendComment.word_cc_tag && anchorText) {
          const color = parsed.isReserved
            ? (parsed.visibility === 'INTERNAL' ? '#F59E0B' : '#3B82F6')
            : (tagColors[parsed.tag] || defaultColor)

          try {
            // Select the comment's content range and insert CC
            const range = comment.contentRange
            range.select(Word.SelectionMode.select)
            await context.sync()
            await insertCommentControl(backendComment.word_cc_tag, anchorText, color)
          } catch (e) {
            devLog.warn('WordCommentSync', `CC insert failed: ${e}`)
          }
        }

        // Delete the native Word comment
        try {
          comment.delete()
          await context.sync()
          devLog.info('WordCommentSync', `Deleted native Word comment id=${commentId}`)
        } catch (e) {
          devLog.warn('WordCommentSync', `Failed to delete native comment: ${e}`)
        }

        // Mark as synced
        syncedIdsRef.current.add(commentId)
        return true
      } catch (e) {
        devLog.error('WordCommentSync', `syncComment failed: ${e}`)
        return false
      }
    },
    [negotiationId, knownTags, tagColors, defaultColor],
  )

  // Poll loop
  useEffect(() => {
    if (!enabled || !isSupported) return

    setIsActive(true)

    const poll = async () => {
      if (isSyncingRef.current) return
      isSyncingRef.current = true

      try {
        await Word.run(async (context) => {
          const body = context.document.body as any
          const comments = body.getComments()
          comments.load('items')
          await context.sync()

          if (!comments.items || comments.items.length === 0) {
            isSyncingRef.current = false
            return
          }

          let syncedThisRound = 0
          for (const comment of comments.items) {
            const didSync = await syncComment(context, comment)
            if (didSync) syncedThisRound++
          }

          if (syncedThisRound > 0) {
            setSyncedCount((prev) => prev + syncedThisRound)
            devLog.info('WordCommentSync', `Synced ${syncedThisRound} comment(s) this round`)
            onCommentSyncedRef.current?.()
          }
        })
      } catch (e) {
        devLog.warn('WordCommentSync', `Poll error: ${e}`)
      } finally {
        isSyncingRef.current = false
      }
    }

    // Fetch paragraph map for para_id lookups (best-effort)
    if (!paraMapFetchedRef.current && negotiationId > 0) {
      getVersionParagraphs(negotiationId, _currentVersion)
        .then(data => { paraMapRef.current = data.paragraphs })
        .catch(() => { /* Best-effort — comments still work without para_id */ })
      paraMapFetchedRef.current = true
    }

    // Initial poll
    poll()

    // Set up interval
    const intervalId = setInterval(poll, POLL_INTERVAL_MS)

    return () => {
      clearInterval(intervalId)
      setIsActive(false)
    }
  }, [enabled, isSupported, syncComment])

  return { isActive, isSupported, syncedCount }
}
